/**
 * Persist Finance account card order (org + user scoped).
 * Mirrors dashboardWidgetOrder: stable account IDs, new accounts append.
 */

import {
  readOrgScopedJson,
  resolveCacheOrganizationId,
  writeOrgScopedJson,
} from "./orgCacheStorage";

export const FINANCE_ACCOUNT_ORDER_CACHE_KEY = "CP_FINANCE_ACCOUNT_ORDER";

function buildActorBucketKey(actor) {
  return (
    actor?.supabase_user_id ||
    actor?.id ||
    actor?.user_id ||
    "anonymous"
  );
}

/**
 * Merge a saved order with the live account id list.
 * Keeps known ids in saved sequence, appends new ids, drops unknowns.
 *
 * @param {string[]|null|undefined} savedOrder
 * @param {string[]} availableIds
 * @returns {string[]}
 */
export function resolveFinanceAccountOrder(savedOrder, availableIds = []) {
  const allowed = Array.isArray(availableIds)
    ? availableIds.filter(Boolean).map(String)
    : [];
  const allowedSet = new Set(allowed);
  const seen = new Set();
  const next = [];

  if (Array.isArray(savedOrder)) {
    for (const rawId of savedOrder) {
      const id = rawId == null ? "" : String(rawId);
      if (!id || !allowedSet.has(id) || seen.has(id)) {
        continue;
      }
      next.push(id);
      seen.add(id);
    }
  }

  for (const id of allowed) {
    if (seen.has(id)) {
      continue;
    }
    next.push(id);
    seen.add(id);
  }

  return next;
}

export function loadFinanceAccountOrder(actor, availableIds = []) {
  const organizationId = resolveCacheOrganizationId(actor);
  const stored = readOrgScopedJson(
    FINANCE_ACCOUNT_ORDER_CACHE_KEY,
    organizationId,
  );

  let savedOrder = null;
  if (Array.isArray(stored)) {
    savedOrder = stored;
  } else if (stored && typeof stored === "object") {
    const bucket = buildActorBucketKey(actor);
    savedOrder = stored[bucket] || stored.default || null;
  }

  return resolveFinanceAccountOrder(savedOrder, availableIds);
}

export function saveFinanceAccountOrder(actor, order, availableIds = null) {
  const organizationId = resolveCacheOrganizationId(actor);
  const resolved = resolveFinanceAccountOrder(
    order,
    availableIds == null ? order : availableIds,
  );
  const bucket = buildActorBucketKey(actor);
  const existing =
    readOrgScopedJson(FINANCE_ACCOUNT_ORDER_CACHE_KEY, organizationId) || {};

  const next =
    existing && typeof existing === "object" && !Array.isArray(existing)
      ? { ...existing, [bucket]: resolved }
      : { [bucket]: resolved };

  writeOrgScopedJson(FINANCE_ACCOUNT_ORDER_CACHE_KEY, organizationId, next);

  return resolved;
}
